import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import Swal from 'sweetalert2';
import i18next from 'i18next';
import { saveDraft, deleteDraft, DRAFT_DEBOUNCE_MS } from '../redux/appointmentDraftSlice';

// AUTOSAVE FOR A VISIT'S DRAFTS (round 9). One instance per screen part that
// types into a visit — the note boxes (MedicalHistory) and the prescription
// (PrescriptionSection) — keyed by section name.
//
// The contract for a caller:
//   schedule(section, content, { clean })  after every edit. 2 s after the
//        LAST edit the content is PUT; `clean` (the text is back to what is
//        saved) sends a DELETE instead, so a stale draft never outlives it.
//   committed(section) / discard(section)  after a Save, or the doctor's
//        "Discard draft": cancel the pending write and DELETE the draft.
//   markRestored(section)  after putting a server draft back in a field.
//   chips[section]  { kind: 'saved' | 'restored', at }  for the chip.
//
// Nothing typed is lost on the way out: leaving the visit (another card, the
// back arrow, a route change) FLUSHES the pending write immediately instead of
// dropping the timer. The route guard and the browser's "leave site?" prompt
// cover only the < 2 s window in which a keystroke is not yet on the server.

/* ---------------------------------------------------------------------------
 * Route-change guard.
 *
 * The app runs on <HashRouter>, which is not a data router, so react-router's
 * `useBlocker` throws there. The navigator the router hands its hooks is the
 * one `navigate()` calls, so wrapping its push/replace is the only in-app
 * interception point. ONE wrapper is installed per navigator and consults every
 * mounted autosave instance, because two independently installed wrappers
 * un-install in the wrong order and leave a stale one behind.
 * ------------------------------------------------------------------------- */
const guards = new Set();
let installed = null; // { navigator, push, replace }

const confirmLeave = async () => {
    const result = await Swal.fire({
        title: i18next.t('appointmentDraft.leave_title', 'Leave this visit?'),
        text: i18next.t('appointmentDraft.leave_text', 'Some of what you typed is only a draft. It is kept as a draft when you leave.'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: i18next.t('appointmentDraft.leave_confirm', 'Keep as draft and leave'),
        cancelButtonText: i18next.t('appointmentDraft.leave_stay', 'Stay'),
        confirmButtonColor: '#4ABDBD',
        cancelButtonColor: '#9ca3af',
    });
    return Boolean(result?.isConfirmed);
};

const guarded = (go) => {
    const pending = [...guards].filter((g) => g.isPending());
    if (pending.length === 0) return go();
    confirmLeave().then((ok) => {
        if (!ok) return;
        pending.forEach((g) => g.flush());
        // "Keep as draft and leave" is a decision about EVERYTHING typed so
        // far, pending write or not: each screen records it as acknowledged
        // so its own unsaved-changes dialog does not ask about it again.
        [...guards].forEach((g) => g.onKeep?.());
        go();
    });
    return undefined;
};

const installGuard = (navigator) => {
    if (!navigator || installed) return;
    const { push, replace } = navigator;
    if (typeof push !== 'function' || typeof replace !== 'function') return;
    installed = { navigator, push, replace };
    navigator.push = (...args) => guarded(() => push.apply(navigator, args));
    navigator.replace = (...args) => guarded(() => replace.apply(navigator, args));
};

const uninstallGuard = () => {
    if (!installed || guards.size > 0) return;
    installed.navigator.push = installed.push;
    installed.navigator.replace = installed.replace;
    installed = null;
};

/* ---------------------------------------------------------------------------
 * Hold during a bulk commit ("Save & complete", owner 24 Sep 2026).
 *
 * The commit deletes the visit's drafts server-side. A debounced PUT landing
 * after it would bring a committed draft back, so while a commit for a visit
 * is in flight its writes are HELD (kept pending, not sent). On success the
 * committed sections are dropped with `clearLocal`; `resumeAutosave` then
 * sends whatever is still held — after a refused commit, that is everything,
 * so nothing typed is lost.
 * ------------------------------------------------------------------------- */
const suspended = new Set();
const resumeListeners = new Set();
export const suspendAutosave = (appointmentId) => {
    if (appointmentId) suspended.add(appointmentId);
};
export const resumeAutosave = (appointmentId) => {
    if (!suspended.delete(appointmentId)) return;
    resumeListeners.forEach((fn) => fn(appointmentId));
};

// `onKeep` (optional): called when the doctor answers the route guard with
// "Keep as draft and leave" — the caller records what is on screen as its
// acknowledged draft (MedicalHistory's "ask once" baseline).
export default function useDraftAutosave({ appointmentId, enabled, onKeep }) {
    const dispatch = useDispatch();
    const navigation = useContext(UNSAFE_NavigationContext);
    const navigator = navigation?.navigator || null;

    // section → { timer, content, clean, appointmentId }
    const pendingRef = useRef({});
    const mountedRef = useRef(true);
    const appointmentRef = useRef(appointmentId);
    const onKeepRef = useRef(onKeep);
    useEffect(() => { onKeepRef.current = onKeep; });
    const [chips, setChips] = useState({});
    const [pendingCount, setPendingCount] = useState(0);

    const setChip = useCallback((section, chip) => {
        if (!mountedRef.current) return;
        setChips((prev) => {
            if (!chip) {
                if (!(section in prev)) return prev;
                const next = { ...prev };
                delete next[section];
                return next;
            }
            return { ...prev, [section]: chip };
        });
    }, []);

    const syncCount = useCallback(() => {
        if (mountedRef.current) setPendingCount(Object.keys(pendingRef.current).length);
    }, []);

    const fire = useCallback((section) => {
        const p = pendingRef.current[section];
        if (!p) return;
        clearTimeout(p.timer);
        if (suspended.has(p.appointmentId)) {
            // Held until resumeAutosave (see above).
            p.timer = null;
            return;
        }
        delete pendingRef.current[section];
        syncCount();
        if (p.clean) {
            dispatch(deleteDraft({ appointmentId: p.appointmentId, section }));
            if (p.appointmentId === appointmentRef.current) setChip(section, null);
            return;
        }
        dispatch(saveDraft({ appointmentId: p.appointmentId, section, content: p.content })).then((res) => {
            // A save that lands after the doctor moved to another visit must
            // not paint a chip on the new one.
            if (saveDraft.fulfilled.match(res) && p.appointmentId === appointmentRef.current) {
                setChip(section, { kind: 'saved', at: res.payload.updatedAt });
            }
        });
    }, [dispatch, setChip, syncCount]);

    const flushAll = useCallback(() => {
        Object.keys(pendingRef.current).forEach(fire);
    }, [fire]);

    const cancel = useCallback((section) => {
        const p = pendingRef.current[section];
        if (!p) return;
        clearTimeout(p.timer);
        delete pendingRef.current[section];
        syncCount();
    }, [syncCount]);

    const schedule = useCallback((section, content, { clean = false } = {}) => {
        if (!enabled || !appointmentId) return;
        const prev = pendingRef.current[section];
        if (prev) clearTimeout(prev.timer);
        pendingRef.current[section] = {
            content,
            clean,
            appointmentId,
            timer: setTimeout(() => fire(section), DRAFT_DEBOUNCE_MS),
        };
        syncCount();
    }, [enabled, appointmentId, fire, syncCount]);

    const dropDraft = useCallback((section) => {
        cancel(section);
        setChip(section, null);
        if (!appointmentId) return Promise.resolve();
        return dispatch(deleteDraft({ appointmentId, section }));
    }, [appointmentId, cancel, dispatch, setChip]);

    // After a bulk commit: the server already deleted the draft — forget the
    // pending write and the chip without another DELETE.
    const clearLocal = useCallback((section) => {
        cancel(section);
        setChip(section, null);
    }, [cancel, setChip]);

    // A commit for this visit ended: send what was held back meanwhile.
    useEffect(() => {
        const onResume = (id) => {
            Object.keys(pendingRef.current)
                .filter((section) => pendingRef.current[section].appointmentId === id && !pendingRef.current[section].timer)
                .forEach(fire);
        };
        resumeListeners.add(onResume);
        return () => { resumeListeners.delete(onResume); };
    }, [fire]);

    const markRestored = useCallback((section, at = null) => {
        setChip(section, { kind: 'restored', at });
    }, [setChip]);

    const isPending = useCallback(() => Object.keys(pendingRef.current).length > 0, []);

    // Switching visit (or leaving the screen) writes what is pending for the
    // visit being left — it is keyed to that visit's id, not the new one.
    useEffect(() => {
        appointmentRef.current = appointmentId;
        setChips({});
        return () => { flushAll(); };
    }, [appointmentId, flushAll]);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Closing the tab mid-sentence: the browser's own prompt, armed only in
    // the 2 s window before the draft is on the server.
    useEffect(() => {
        if (pendingCount === 0) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [pendingCount]);

    // In-app route change in the same window.
    useEffect(() => {
        if (!navigator) return undefined;
        const guard = { isPending, flush: flushAll, onKeep: () => onKeepRef.current?.() };
        guards.add(guard);
        installGuard(navigator);
        return () => {
            guards.delete(guard);
            uninstallGuard();
        };
    }, [navigator, isPending, flushAll]);

    return {
        chips,
        schedule,
        committed: dropDraft,
        discard: dropDraft,
        clearLocal,
        markRestored,
        isPending,
        flushAll,
    };
}
